import React from 'react';
import { motion } from 'framer-motion';
import { FiAward, FiUsers, FiClock, FiBook } from 'react-icons/fi';
import '../styles/Pages.css';
import EnhancedHeader from '../components/EnhancedHeader';

const Team = () => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.15 } }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.8 } }
  };

  return (
    <div className="page-container">
      <EnhancedHeader
        title="Meet Our Team"
        subtitle="Dedicated professionals delivering excellent care"
        backgroundImage="/images/residential-care-banner.jpg"
        backgroundGradient="linear-gradient(135deg, rgba(26, 188, 156, 0.7) 0%, rgba(17, 168, 126, 0.7) 100%)"
      />


      {/* Team Overview with Image 1 */}
      <motion.section
        className="section-split"
      >
        <div className="container">
          <div className="split-grid">
            <motion.div
              className="split-content"
              variants={containerVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.3 }}
            >
              <h2 className="section-title">Staff Excellence</h2>
              <p className="section-text">
                All staff will be trained on various needed policies and top notch on safeguarding behavior management and trauma informed care.
              </p>

              <div className="team-features">
                {[
                  { icon: FiAward, title: 'Qualified Team', desc: 'Registered Manager and qualified residential support staff' },
                  { icon: FiBook, title: 'Professional Development', desc: 'Continuous training in safeguarding and trauma care' },
                  { icon: FiClock, title: '24/7 Coverage', desc: 'Awaking night staff with access to therapists' },
                  { icon: FiUsers, title: 'Dedicated Support', desc: 'Key workers for each resident' }
                ].map((feature, i) => (
                  <motion.div
                    key={i}
                    className="team-feature"
                    variants={itemVariants}
                    whileHover={{ x: 10 }}
                  >
                    <feature.icon className="feature-icon" />
                    <div>
                      <h3>{feature.title}</h3>
                      <p>{feature.desc}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            <div className="split-image">
              {/* IMAGE PLACEMENT 1: Team Photo */}
              <img src="/images/care-team-4.jpg" alt="Our Team" />
            </div>
          </div>
        </div>
      </motion.section>

      {/* Professional Standards */}
      <motion.section
        className="standards-section"
        variants={containerVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.3 }}
      >
        <div className="container">
          <h2 className="section-title">Professional Standards</h2>

          <div className="standards-grid">
            {[
              { title: 'Safeguarding', items: ['Child protection training', 'DBS checks', 'Safeguarding protocols', 'Regular audits'] },
              { title: 'Mental Health', items: ['Trauma-informed approach', 'Crisis management', 'Therapeutic support', 'Regular assessments'] },
              { title: 'Care Quality', items: ['Care plans reviewed', 'Key worker assigned', 'Progress monitoring', 'Outcome tracking'] }
            ].map((standard, i) => (
              <motion.div
                key={i}
                className="standard-card"
                variants={itemVariants}
                whileHover={{ y: -15, boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1)' }}
              >
                <h3>{standard.title}</h3>
                <ul>
                  {standard.items.map((item, j) => (
                    <li key={j}>
                      <span>•</span> {item}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.section>

      {/* Training & Development with Image 2 */}
      <motion.section
        className="section-split reverse"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
        viewport={{ once: true, amount: 0.3 }}
      >
        <div className="container">
          <div className="split-grid">
            <div className="split-image">
              {/* IMAGE PLACEMENT 2: Training/Development */}
              <img src="/images/care-team-2.jpg" alt="Training & Development" />
            </div>

            <motion.div
              className="split-content"
              variants={containerVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.3 }}
            >
              <h2 className="section-title">Continuous Training</h2>
              <p className="section-text">
                Our team participates in ongoing professional development to maintain the highest standards of care and service delivery.
              </p>

              <div className="training-list">
                {[
                  'Behavior management techniques',
                  'Safeguarding and child protection',
                  'Trauma-informed care',
                  'Mental health support',
                  'Communication skills',
                  'Crisis intervention'
                ].map((training, i) => (
                  <motion.div
                    key={i}
                    className="training-item"
                    variants={{
                      hidden: { opacity: 0, x: -20 },
                      visible: { opacity: 1, x: 0 }
                    }}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.05 }}
                  >
                    <span className="checkmark">→</span> {training}
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </motion.section>
    </div>
  );
};

export default Team;
